import React, {useEffect, useRef, useState} from 'react';
import mapboxgl from 'mapbox-gl';
import './map-view.scss';
import turf from 'turf';
import Infobox from "../../components/infobox";
import {MapTimelineControl} from "../../components/map-timeline-control";
import {InfoboxSingleObservation} from "../../components/infobox-single-observation";
import {MapMode, MapModeType} from "./index";
import {InfoboxSpeciesLocationDetail} from "../../components/infobox-species-location-detail";
import {InfoboxLocationDetail} from "../../components/infobox-location-detail";
import {EBirdMyDataSchema, EBirdObservationsByLocation, getFilteredObservations} from "ebird-mydata-reader";
import {EBirdMyDataSchemaWithImage} from "../big-year";
import type {SpeciesFamilyLink} from "../../App";

export type MapViewProps = {
    mode: MapModeType
    markers: any,
    year?: string|number,
    month?: string|number,
    showPlayControls?: boolean,
    activeSpecies: number,
    activeLocation: string,
    setActiveLocation: React.Dispatch<string|undefined>,
    observationsByLocation: EBirdObservationsByLocation[],
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    setObservationImages: (observations: EBirdMyDataSchema[]) => EBirdMyDataSchemaWithImage[],
    onShowTimePeriodSelector: () => void
}

export default function MapView({
    mode,
    markers,
    year,
    month,
    showPlayControls,
    activeSpecies,
    activeLocation,
    setActiveLocation,
    observationsByLocation,
    familyByTaxonomicOrder,
    setObservationImages,
    onShowTimePeriodSelector
}: MapViewProps) {
    mapboxgl.accessToken = 'pk.eyJ1Ijoib2hpb2RhdmUiLCJhIjoiY2swMDZqdWw1MjVxeDNqcW9uaG8xcW9nbyJ9.Hlqy7TBoh8QIPWC5j7AQNQ';
    const ANIMATION_DELAY = 4500;
    const SHORT_DELAY = 2500;
    const EXTRA_SHORT_DELAY = 500;
    const STARTING_ZOOM = 11;
    const SMALL_SCREEN_ZOOM_REDUCTION = 2;
    const SMALL_SCREEN_CENTER_OFFSET: [number, number] = [0, 70];
    const SMALL_SCREEN_QUERY = '(max-width: 767.98px)';
    const MAP_MAX_ZOOM = 12;
    const ZOOM_INCREMENT = 0;
    const SAME_LOCATION_SPEED = 2.2;
    const NEW_LOCATION_SPEED = 1.8;
    const ZOOMED_OUT_THRESHOLD = 7;
    const MARKER_SOURCE_ID = 'birdmap-observations';
    const MARKER_LAYER_ID = 'birdmap-observation-points';
    const ACTIVE_MARKER_LAYER_ID = 'birdmap-active-observation-point';

    const mapRoot = useRef<any>(null);
    const map = useRef<any>(null);
    const miniMapRoot = useRef<any>(null);
    const miniMap = useRef<any>(null);
    const modeRef = useRef<MapModeType>(mode);

    let lastMarker: any = useRef(null);
    let currentMarker: any = useRef(null);
    let currentIndex: any = useRef(-1);

    const [currentMarkerState, setCurrentMarkerState]: any = useState();
    const [currentIndexState, setCurrentIndexState]: any = useState();
    const [totalMarkers, setTotalMarkers]: any = useState();
    const [paused, setPaused] = useState(false);
    const [lastZoom, setLastZoom] = useState(STARTING_ZOOM);
    const [activeLocationObservations, setActiveLocationObservations] = useState<any[]>();
    const [isMapReady, setIsMapReady] = useState(false);

    let mapTimeout: any = useRef(null);

    useEffect(() => {
        modeRef.current = mode;
        updateMarkerLayerStyles();
        // Marker styling is an imperative Mapbox side effect keyed to mode only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        if (map.current) { return; }

        map.current = new mapboxgl.Map({
            container: mapRoot.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            /*projection: {
              name: 'globe'
            },*/
            center: [-98.5795, 39.8283],
            pitch: 25,
            zoom: 4,
            maxZoom: getMapMaxZoom(),
            keyboard: false,
            attributionControl: false
        });

        updateZoomProps();
        map.current.on('zoom', updateZoomProps);
        window.addEventListener('resize', updateResponsiveMapSettings);

        miniMap.current = new mapboxgl.Map({
            container: miniMapRoot.current,
            style: 'mapbox://styles/mapbox/light-v10',
            center: [-98.5795, 39.8283],
            zoom: 1,
            interactive: false,
            attributionControl: false
        });

        initMiniMapFollow();
        map.current.once('load', () => {
            setupCanvasMarkerLayers();
            setIsMapReady(true);
        });

        document.body.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('resize', updateResponsiveMapSettings);
            stop();
            reset();
        }
        // Mapbox setup is intentionally mounted once; the handlers read current refs/state during map interactions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSmallScreenMap = () => {
        return window.matchMedia(SMALL_SCREEN_QUERY).matches;
    }

    const getMapMaxZoom = () => {
        return isSmallScreenMap() ? MAP_MAX_ZOOM - SMALL_SCREEN_ZOOM_REDUCTION : MAP_MAX_ZOOM;
    }

    const getPlaybackZoom = (zoomLevel: number) => {
        return isSmallScreenMap() ? Math.max(zoomLevel - SMALL_SCREEN_ZOOM_REDUCTION, 1) : zoomLevel;
    }

    const getPlaybackOffset = (): [number, number] => {
        return isSmallScreenMap() ? SMALL_SCREEN_CENTER_OFFSET : [0, 0];
    }

    const updateResponsiveMapSettings = () => {
        map.current?.setMaxZoom(getMapMaxZoom());
    }

    const handleKeyPress = (e: KeyboardEvent) => {
        // console.log(e);
        if (mode === MapMode.Year) {
            switch (e.code) {
                case 'ArrowLeft':
                    e.shiftKey ? toStart() : rewind();
                    break;
                case 'ArrowRight':
                    e.shiftKey ? toEnd() : fastForward();
                    break;
                case 'Space':
                    togglePaused();
                    break;
            }
        }
    }

    const updateZoomProps = () => {
        if (!mapRoot.current || !map.current || !miniMapRoot.current) return;

        mapRoot.current.className = `map-root zoom-${parseInt(map.current.getZoom())}`

        if (map.current.getZoom() < ZOOMED_OUT_THRESHOLD) {
            miniMapRoot.current.className = 'minimap-root state-hidden';
        } else {
            miniMapRoot.current.className = 'minimap-root';
        }
    }

    const initMiniMapFollow = () => {
        updateMiniMap();
        map.current.on('move', updateMiniMap);
    }

    const updateMiniMap = () => {
        miniMap.current.jumpTo({
            center: map.current.getCenter(),
            zoom: 4.5,
            speed: 0.5
        });
    }

    const handleMarkerClick = (locationId: string) => {
        if (modeRef.current === MapMode.Year) return;

        console.log(locationId);
        console.log(modeRef.current);

        // mode = MapMode.Location;

        const foundMarker = markers.features.find((el: any) => el.properties.locationId === locationId);
        setActiveLocation(locationId);

        if (foundMarker) {
            currentMarker.current = foundMarker;
            setCurrentMarkerState(foundMarker);
            updateMarkerStates();
        }
    }

    useEffect(() => {
        if (!isMapReady) return;
        // console.log(markers);
        setTotalMarkers(markers?.features?.length ?? 0);

        stop();
        reset();

        if (markers?.features?.length) {
            addAllMarkers();
            if (mode === MapMode.Year) {
                playNext();
            } else {
                done();
            }
        }
        // Marker playback is managed through refs and Mapbox side effects; rerunning for every handler identity breaks playback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapReady, markers, mode]);

    useEffect(() => {
        setActiveLocation(undefined);
    }, [mode, setActiveLocation]);

    useEffect(() => {
        if (activeLocation) {
            if (mode === MapMode.Species && activeSpecies) {
                const filteredFeatures = markers.features.filter((el: any) => el.properties.locationId === activeLocation);
                setActiveLocationObservations(filteredFeatures);
            } else if (activeLocation) {
                setPaused(true);
                stop();

                const foundLocation = observationsByLocation.find(el => el.locationId === activeLocation);
                if (foundLocation) {
                    let filteredObservations = getFilteredObservations(foundLocation.observations, parseInt(year as string), parseInt(month as string), true);
                    filteredObservations = setObservationImages(filteredObservations);
                    setActiveLocationObservations(filteredObservations);
                }
            }
        }
    }, [activeLocation, activeSpecies, markers.features, mode, month, observationsByLocation, setObservationImages, year]);

    const reset = () => {
        currentIndex.current = -1;
        setCurrentIndexState(-1);

        currentMarker.current = null;
        setCurrentMarkerState(null);

        lastMarker.current = null;

        clearCanvasMarkers();

        setActiveLocation(undefined);

    }

    const stop = () => {
        map.current.stop();
        miniMap.current.stop();
        clearTimeout(mapTimeout.current);
    }

    const playNext = () => {
        currentIndex.current = currentIndex.current + 1;
        currentMarker.current = markers.features[currentIndex.current];
        if (!currentMarker.current) return;

        setActiveLocation(undefined);
        setCurrentIndexState(currentIndex.current);
        setCurrentMarkerState(currentMarker.current);

        updateMarkerStates();

        let zoomLevel, speed;
        if (lastMarker.current?.properties.locationName === currentMarker.current?.properties.locationName) {
            const zl = lastZoom + ZOOM_INCREMENT;
            setLastZoom(zl);
            zoomLevel = getPlaybackZoom(zl);
            speed = SAME_LOCATION_SPEED;
        } else {
            const zl = STARTING_ZOOM;
            setLastZoom(zl);
            zoomLevel = getPlaybackZoom(zl);
            speed = NEW_LOCATION_SPEED;
        }

        map.current.flyTo({
            center: currentMarker.current.geometry.coordinates,
            zoom: zoomLevel,
            offset: getPlaybackOffset(),
            curve: 2,
            speed: speed
        });

        map.current.once('moveend', () => {
            // Duration the slide is on screen after interaction
            let delay = !movedSinceLastMarker() ? SHORT_DELAY : ANIMATION_DELAY;

            if (currentIndex.current >= markers.features.length - 1) {
                mapTimeout.current = setTimeout( () => {
                    done();
                }, delay);
            } else {
                clearTimeout(mapTimeout.current);
                mapTimeout.current = setTimeout( () => {
                    // Increment index
                    lastMarker.current =currentMarker.current;
                    playNext();
                }, delay);
            }

        });
    }

    const movedSinceLastMarker = () => {
        return (lastMarker.current?.properties?.locationName !== currentMarker.current?.properties?.locationName);
    }

    const togglePaused = () => {
        setPaused(!paused);
        if (paused) {
            clearTimeout(mapTimeout.current);
            mapTimeout.current = setTimeout(() => {
                playNext();
            }, EXTRA_SHORT_DELAY);
        } else {
            stop();
        }
    }

    const toStart = () => {
        currentIndex.current = -1;
        stop();
        setPaused(false);
        playNext();
    }

    const rewind = () => {
        currentIndex.current = Math.max( currentIndex.current - 2, -1);

        stop();
        setPaused(false);
        playNext();
    }

    const fastForward = () => {
        if (currentIndex.current >= markers?.features?.length - 1) { return; }

        stop();
        setPaused(false)
        playNext();
    }

    const toEnd = () => {
        currentIndex.current = Math.max(markers?.features?.length - 2, -1);

        stop();
        setPaused(false);
        playNext();
    }

    const done = () => {
        lastMarker.current = null;

        try {
            const bounds = turf.bbox(markers);
            map.current.fitBounds(bounds, { padding: 150 });
        } catch(e) {
            console.log('Unable to zoom to bounds');
        }
    }

    const addAllMarkers = () => {
        addCanvasMarkers();
    }

    const setupCanvasMarkerLayers = () => {
        if (!map.current || !map.current.isStyleLoaded()) return;

        if (!map.current.getSource(MARKER_SOURCE_ID)) {
            map.current.addSource(MARKER_SOURCE_ID, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });
        }

        if (!map.current.getLayer(MARKER_LAYER_ID)) {
            map.current.addLayer({
                id: MARKER_LAYER_ID,
                type: 'circle',
                source: MARKER_SOURCE_ID,
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        3, 4,
                        8, 7,
                        14, 11
                    ],
                    'circle-color': '#51a488',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.9
                }
            });

            map.current.on('click', MARKER_LAYER_ID, (event: any) => {
                const locationId = event.features?.[0]?.properties?.locationId;
                if (locationId) handleMarkerClick(locationId);
            });

            map.current.on('mouseenter', MARKER_LAYER_ID, () => {
                if (modeRef.current !== MapMode.Year) {
                    map.current.getCanvas().style.cursor = 'pointer';
                }
            });

            map.current.on('mouseleave', MARKER_LAYER_ID, () => {
                map.current.getCanvas().style.cursor = '';
            });
        }

        if (!map.current.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.addLayer({
                id: ACTIVE_MARKER_LAYER_ID,
                type: 'circle',
                source: MARKER_SOURCE_ID,
                filter: ['==', ['get', 'locationId'], ''],
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        3, 6,
                        8, 10,
                        14, 15
                    ],
                    'circle-color': '#c03221',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.92
                }
            });
        }

        updateMarkerLayerStyles();
    }

    const getDefaultMarkerRadius = () => {
        return modeRef.current === MapMode.Species
            ? [
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 5,
                8, 8,
                14, 12
            ]
            : [
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 4,
                8, 7,
                14, 11
            ];
    }

    const updateMarkerLayerStyles = () => {
        if (!map.current?.getLayer(MARKER_LAYER_ID)) return;

        map.current.setPaintProperty(MARKER_LAYER_ID, 'circle-radius', getDefaultMarkerRadius());
        map.current.setPaintProperty(
            MARKER_LAYER_ID,
            'circle-color',
            modeRef.current === MapMode.Species ? '#c03221' : '#51a488'
        );
        map.current.setPaintProperty(
            MARKER_LAYER_ID,
            'circle-opacity',
            modeRef.current === MapMode.Species ? 0.94 : 0.9
        );
    }

    const addCanvasMarkers = () => {
        if (!map.current || !isMapReady) {
            return;
        }

        setupCanvasMarkerLayers();
        const markerSource = map.current.getSource(MARKER_SOURCE_ID) as mapboxgl.GeoJSONSource|undefined;
        markerSource?.setData(markers);
        updateMarkerStates();
    }

    const clearCanvasMarkers = () => {
        if (!map.current?.getSource(MARKER_SOURCE_ID)) return;

        const markerSource = map.current.getSource(MARKER_SOURCE_ID) as mapboxgl.GeoJSONSource;
        markerSource.setData({
            type: 'FeatureCollection',
            features: []
        });

        if (map.current.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.setFilter(ACTIVE_MARKER_LAYER_ID, ['==', ['get', 'locationId'], '']);
        }
    }

    const updateMarkerStates = () => {
        if (map.current?.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.setFilter(
                ACTIVE_MARKER_LAYER_ID,
                ['==', ['get', 'locationId'], currentMarker.current?.properties?.locationId ?? '']
            );
        }
    }

    return (
        <>
            <div className="map-root" ref={mapRoot}></div>
            <div className="minimap-root" ref={miniMapRoot}></div>
            {mode === MapMode.Year || activeLocation ?
                <Infobox>
                    {
                        (mode === MapMode.Year && !activeLocation) ?
                            <InfoboxSingleObservation
                                currentMarker={currentMarkerState}
                                currentIndex={currentIndexState}
                                total={totalMarkers}
                                year={year}
                                month={month}
                                familyLink={familyByTaxonomicOrder.get(currentMarkerState?.properties?.taxonomicOrder)}
                            /> : null
                    }
                    {
                        mode === MapMode.Species && activeSpecies && activeLocation ?
                            <InfoboxSpeciesLocationDetail
                                taxonomicOrder={activeSpecies}
                                observations={activeLocationObservations}
                                familyLink={familyByTaxonomicOrder.get(activeSpecies)}
                                onClose={() => setActiveLocation(undefined)}
                            />
                            : null
                    }
                    {
                        (mode === MapMode.Location || mode === MapMode.Year) && activeLocation ?
                            <InfoboxLocationDetail
                                observations={activeLocationObservations}
                                year={year}
                                month={month}
                            /> : null
                    }
                </Infobox>
            : null}
            {
                showPlayControls ?
                    <MapTimelineControl
                        paused={paused}
                        currentMarker={currentMarkerState}
                        markers={markers}
                        year={year}
                        month={month}
                        togglePaused={togglePaused}
                        rewind={rewind}
                        fastForward={fastForward}
                        toStart={toStart}
                        toEnd={toEnd}
                        onShowTimePeriodSelector={onShowTimePeriodSelector}
                    />
                : null
            }

        </>
    )
}
